/**
 * Following a growing thread down, as pure rules. Two things can stop it: the
 * owner switching it off (`enabled`), and scrolling up to read (`paused`, which
 * clears by itself when they come back to the end). While it is stopped, new
 * content is only noted (`unseen`) so a "jump to latest" pill can offer it.
 */

/** Closer to the end than this counts as being at it (px). */
export const NEAR_END = 80

export interface Follow {
  enabled: boolean
  paused: boolean
  unseen: boolean
}

export const START: Follow = { enabled: true, paused: false, unseen: false }

export const following = (f: Follow) => f.enabled && !f.paused

/** The distance from the bottom of the page, from the browser's three numbers. */
export const distanceFromEnd = (scrollHeight: number, scrollY: number, viewport: number) =>
  scrollHeight - scrollY - viewport

/**
 * A scroll event. Moving up away from the end pauses; reaching the end again
 * resumes and clears what was waiting. Content growing under a still finger
 * fires no scroll event, so growth alone never reads as scrolling up.
 */
export const onScroll = (f: Follow, dist: number, delta: number): Follow => {
  if (dist <= NEAR_END) return f.paused || f.unseen ? { ...f, paused: false, unseen: false } : f
  if (delta < 0 && !f.paused) return { ...f, paused: true }
  return f
}

/** The content got taller: note it when we are not following it down. */
export const onGrow = (f: Follow): Follow => (following(f) || f.unseen ? f : { ...f, unseen: true })

/** The on/off button. Turning it on resumes at once. */
export const toggle = (f: Follow): Follow =>
  f.enabled ? { ...f, enabled: false } : { enabled: true, paused: false, unseen: false }

/** "Jump to latest", or sending something: go to the end and carry on following if it is on. */
export const jump = (f: Follow): Follow => ({ ...f, paused: false, unseen: false })
